import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Brand } from "@/components/Logo";
import { Loader2, PenLine, BadgeCheck, FileText, ShieldCheck, Upload, Check, Camera, Paperclip } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "input-field";
const LBL = "block text-xs font-bold text-[#003087] mb-1.5";

const ALLEGATI = [
  { kind: "carta_identita", label: "Carta d'identità", hint: "Fronte e retro in un'unica foto o PDF" },
  { kind: "tessera_sanitaria", label: "Tessera sanitaria", hint: "Serve il codice fiscale stampato sopra" },
];

export default function DocumentiPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [form, setForm] = useState({
    luogo_nascita: "", data_nascita: "", via: "", cap: "", citta: "", cf: "",
    doc_ente: "", doc_numero: "", doc_data: "",
    consenso_marketing: false, consenso_cessione: false, accetto: false,
  });
  const [sending, setSending] = useState(false);
  const [signed, setSigned] = useState(false);
  const [firmaImg, setFirmaImg] = useState(null);
  const [allegati, setAllegati] = useState({});
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    fetch(`${API}/documenti/${orderId}?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Link non valido");
        return r.json();
      })
      .then((data) => {
        setState({ loading: false, error: null, data });
        setForm((f) => ({ ...f, cf: data.cf || "", citta: data.city || "" }));
        if (data.allegati) setAllegati(data.allegati);
      })
      .catch((e) => setState({ loading: false, error: e.message, data: null }));
  }, [orderId, token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const caricaAllegato = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/documenti/${orderId}/allegati?t=${encodeURIComponent(token)}&kind=${kind}`, { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Errore caricamento");
      setAllegati((a) => ({ ...a, [kind]: d.filename }));
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(null);
    }
  };

  const invia = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await fetch(`${API}/documenti/${orderId}/firma`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ t: token, nome_firma: state.data.name, firma_img: firmaImg, ...form }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Errore durante la firma");
      setSigned(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  const d = state.data;
  const delegaAnteprima = d
    ? `Io sottoscritto/a ${d.name}, residente in via ${form.via || "____"}, CAP ${form.cap || "____"}, Città ${form.citta || "____"}, nato/a ${form.luogo_nascita || "____"} il ${form.data_nascita || "____"}, Codice Fiscale ${form.cf || "____"}, Documento di identità n. ${form.doc_numero || "____"} rilasciato da ${form.doc_ente || "____"} il ${form.doc_data || "____"}, quale persona fisica, in qualità di MANDANTE, DELEGO e nomino procuratore ex art. 1703 ss. Cod. Civ. la Società Sdebitiamoci Srl...`
    : "";

  return (
    <div className="min-h-screen bg-[#F6FAFD]" data-testid="documenti-page">
      <header className="bg-white border-b border-[#E2E8F0] py-5 px-5">
        <div className="max-w-3xl mx-auto"><Brand /></div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12 space-y-8">
        {state.loading && <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#00AEEF]" size={32} /></div>}

        {state.error && (
          <div className="card rounded-3xl p-10 text-center" data-testid="documenti-error">
            <h1 className="text-xl font-bold text-[#003087]">Link non valido</h1>
            <p className="mt-3 text-sm text-[#64748B]">{state.error}. Contattaci su WhatsApp se il problema continua.</p>
          </div>
        )}

        {d && (signed || d.firmato) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card rounded-3xl p-10 text-center" data-testid="documenti-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#E6F6FD] flex items-center justify-center"><BadgeCheck size={32} className="text-[#00AEEF]" /></div>
            <h1 className="text-2xl font-extrabold text-[#003087] mt-6">Documenti firmati. Partiamo!</h1>
            <p className="mt-4 text-sm text-[#1A1A1A] leading-relaxed max-w-md mx-auto">
              Delega e Informativa Privacy firmate digitalmente (data, ora e IP registrati).
              Richiediamo ora le tue visure a CRIF, Centrale Rischi, CTC e CAI: fascicolo digitale entro 48-72 ore lavorative.
              Ti aggiorniamo noi via email e WhatsApp.
            </p>
          </motion.div>
        )}

        {d && !signed && !d.firmato && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">Fase 1 · Documenti per le visure</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#003087] mt-3" data-testid="documenti-title">{d.name}, due firme e partiamo.</h1>
              <p className="mt-3 text-sm text-[#64748B]">Per richiedere le tue visure alle banche dati la legge richiede la tua delega scritta e la privacy firmata. 2 minuti, tutto online.</p>
            </div>

            <div className="card rounded-3xl p-7" data-testid="privacy-doc">
              <h2 className="text-lg font-bold text-[#003087] flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-[#00AEEF]" /> 1 · Informativa Privacy (GDPR)</h2>
              <div className="max-h-64 overflow-y-auto rounded-2xl bg-[#F6FAFD] border border-[#E2E8F0] p-5 space-y-4" data-testid="privacy-text">
                {d.privacy.map((s, i) => (
                  <div key={i}>
                    <p className="text-xs font-bold text-[#003087]">{s.titolo}</p>
                    {s.testo && <p className="text-xs text-[#1A1A1A] leading-relaxed mt-1 whitespace-pre-line">{s.testo}</p>}
                  </div>
                ))}
              </div>
              <label className="mt-4 flex items-start gap-2.5 text-xs text-[#1A1A1A] cursor-pointer">
                <input data-testid="consenso-privacy" type="checkbox" checked={form.accetto} onChange={set("accetto")} className="mt-0.5 accent-[#00AEEF]" />
                <span><strong>Ho letto e accetto l'informativa privacy</strong> (finalità contrattuali — obbligatorio)</span>
              </label>
              <label className="mt-2.5 flex items-start gap-2.5 text-xs text-[#64748B] cursor-pointer">
                <input data-testid="consenso-marketing" type="checkbox" checked={form.consenso_marketing} onChange={set("consenso_marketing")} className="mt-0.5 accent-[#00AEEF]" />
                <span>Presto il consenso (facoltativo) per finalità di marketing e profilazione [lett. e-f]</span>
              </label>
              <label className="mt-2.5 flex items-start gap-2.5 text-xs text-[#64748B] cursor-pointer">
                <input data-testid="consenso-cessione" type="checkbox" checked={form.consenso_cessione} onChange={set("consenso_cessione")} className="mt-0.5 accent-[#00AEEF]" />
                <span>Presto il consenso (facoltativo) alla cessione dei dati a terzi per marketing [lett. g]</span>
              </label>
            </div>

            <form onSubmit={invia} className="card rounded-3xl p-7" data-testid="delega-form">              <h2 className="text-lg font-bold text-[#003087] flex items-center gap-2 mb-1"><FileText size={18} className="text-[#00AEEF]" /> 2 · Delega per la cancellazione</h2>
              <p className="text-xs text-[#64748B] mb-5">Compila i tuoi dati: la delega si compila da sola sotto.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={LBL}>Luogo di nascita *</label><input data-testid="delega-luogo-nascita" required className={FIELD} value={form.luogo_nascita} onChange={set("luogo_nascita")} placeholder="Es. Milano" /></div>
                <div><label className={LBL}>Data di nascita *</label><input data-testid="delega-data-nascita" required className={FIELD} value={form.data_nascita} onChange={set("data_nascita")} placeholder="gg/mm/aaaa" /></div>
                <div className="sm:col-span-2"><label className={LBL}>Via e numero civico *</label><input data-testid="delega-via" required className={FIELD} value={form.via} onChange={set("via")} placeholder="Es. Via Roma 12" /></div>
                <div><label className={LBL}>CAP *</label><input data-testid="delega-cap" required className={FIELD} value={form.cap} onChange={set("cap")} placeholder="Es. 20100" /></div>
                <div><label className={LBL}>Città *</label><input data-testid="delega-citta" required className={FIELD} value={form.citta} onChange={set("citta")} placeholder="Es. Milano" /></div>
                <div className="sm:col-span-2"><label className={LBL}>Codice Fiscale *</label><input data-testid="delega-cf" required className={FIELD} value={form.cf} onChange={set("cf")} placeholder="16 caratteri" /></div>
                <div><label className={LBL}>Documento rilasciato da *</label><input data-testid="delega-doc-ente" required className={FIELD} value={form.doc_ente} onChange={set("doc_ente")} placeholder="Es. Comune di Milano" /></div>
                <div><label className={LBL}>Numero documento *</label><input data-testid="delega-doc-numero" required className={FIELD} value={form.doc_numero} onChange={set("doc_numero")} placeholder="Es. CA1234567" /></div>
                <div><label className={LBL}>Data rilascio documento *</label><input data-testid="delega-doc-data" required className={FIELD} value={form.doc_data} onChange={set("doc_data")} placeholder="gg/mm/aaaa" /></div>
              </div>

              <div className="mt-5 rounded-2xl bg-[#F6FAFD] border border-[#E2E8F0] p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#00AEEF] mb-2">Anteprima delega (si compila in automatico)</p>
                <p className="text-xs text-[#1A1A1A] leading-relaxed" data-testid="delega-preview">{delegaAnteprima}</p>
                <p className="text-[11px] text-[#64748B] leading-relaxed mt-3">Poteri conferiti: accesso a SIC e Centrale Rischi, richiesta documentazione ex art. 119 TUB, contestazione vizi ed errori, richiesta cancellazione/correzione, azioni avanti ABF e ACF, mediazione, transazione. Obbligazione di mezzi, non di risultato.</p>
              </div>

              <div className="mt-7 border-t border-[#E2E8F0] pt-6">
                <h3 className="text-base font-bold text-[#003087] flex items-center gap-2 mb-1"><Upload size={16} className="text-[#00AEEF]" /> 3 · Allega i tuoi documenti *</h3>
                <p className="text-xs text-[#64748B] mb-4">La delega è valida solo con i documenti allegati. Foto nitide o PDF, max 8 MB.</p>
                <div className="grid gap-3">
                  {ALLEGATI.map((a) => (
                    <div key={a.kind} data-testid={`upload-${a.kind}`}
                      className={`rounded-2xl border-2 border-dashed p-4 transition-colors duration-200 ${allegati[a.kind] ? "border-[#00AEEF] bg-[#E6F6FD]" : "border-[#E2E8F0] bg-white"}`}>
                      <div className="flex items-center gap-2 text-sm font-bold text-[#003087]">
                        {uploading === a.kind ? <Loader2 size={16} className="animate-spin text-[#00AEEF]" />
                          : allegati[a.kind] ? <Check size={16} className="text-[#00AEEF]" />
                          : <Upload size={16} className="text-[#00AEEF]" />}
                        {a.label}
                      </div>
                      <p className="text-[11px] text-[#64748B] mt-1">{allegati[a.kind] ? `✓ ${allegati[a.kind]}` : a.hint}</p>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <label data-testid={`upload-${a.kind}-camera`}
                          className="flex items-center justify-center gap-1.5 rounded-full bg-[#00AEEF] text-white text-xs font-bold py-2.5 cursor-pointer active:scale-[0.98] transition-transform">
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => caricaAllegato(a.kind, e.target.files?.[0])} />
                          <Camera size={15} /> Scatta foto
                        </label>
                        <label data-testid={`upload-${a.kind}-file`}
                          className="flex items-center justify-center gap-1.5 rounded-full border-2 border-[#00AEEF] text-[#003087] text-xs font-bold py-2.5 cursor-pointer active:scale-[0.98] transition-transform">
                          <input type="file" accept="image/*,application/pdf" className="hidden"
                            onChange={(e) => caricaAllegato(a.kind, e.target.files?.[0])} />
                          <Paperclip size={15} /> Allega file
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-xs font-bold text-[#003087] mb-1.5">4 · La tua firma *</label>
                <SignaturePad onChange={setFirmaImg} testid="delega-signature" />
              </div>

              <button data-testid="delega-submit" disabled={sending || !form.accetto || !firmaImg || !allegati.carta_identita || !allegati.tessera_sanitaria}
                className="btn-green mt-6 w-full rounded-full py-4 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Firmo, {d.name} — {new Date().toLocaleDateString("it-IT")}
              </button>
              <p className="mt-3 text-center text-[10px] text-[#94A3B8]">La firma digitale registra data, ora e indirizzo IP.</p>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
}
